import { Component } from '@theme/component';

/**
 * @typedef {object} AnnouncementRefs
 * @property {HTMLElement[]} [messages]
 * @extends {Component<AnnouncementRefs>}
 */
class ProjectPetAnnouncement extends Component {
  #cleanup;
  #timer;
  #resize;
  #motion;
  #current = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initialize();
  }

  #initialize() {
    this.#stop();
    this.#cleanup?.abort();
    this.#resize?.disconnect();
    this.#cleanup = new AbortController();
    const { signal } = this.#cleanup;
    this.#motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.#current = 0;
    this.#showCurrent();

    this.addEventListener('mouseenter', this.#stop, { signal });
    this.addEventListener('mouseleave', this.#schedule, { signal });
    this.addEventListener('focusin', this.#stop, { signal });
    this.addEventListener('focusout', this.#schedule, { signal });
    document.addEventListener('visibilitychange', this.#schedule, { signal });
    this.#motion.addEventListener('change', this.#schedule, { signal });
    document.addEventListener('projectpet:motion-change', this.#schedule, { signal });
    this.#resize = new ResizeObserver(() => {
      document.documentElement.style.setProperty('--pp-announcement-height', `${this.offsetHeight}px`);
    });
    this.#resize.observe(this);
    this.#schedule();
  }

  #showCurrent() {
    this.refs.messages?.forEach((message, index) => message.setAttribute('aria-hidden', String(index !== this.#current)));
  }

  #stop = () => {
    clearTimeout(this.#timer);
    this.#timer = undefined;
  };

  #schedule = () => {
    this.#stop();
    if (this.#motion.matches || document.documentElement.dataset.ppMotionPaused === 'true' || document.hidden || (this.refs.messages?.length ?? 0) < 2) return;
    this.#timer = setTimeout(() => {
      if (!this.matches(':hover') && !this.matches(':focus-within')) {
        this.#current = (this.#current + 1) % this.refs.messages.length;
        this.#showCurrent();
      }
      this.#schedule();
    }, 5000);
  };

  disconnectedCallback() {
    this.#stop();
    this.#cleanup?.abort();
    this.#resize?.disconnect();
    document.documentElement.style.removeProperty('--pp-announcement-height');
    super.disconnectedCallback();
  }
}

if (!customElements.get('pp-announcement')) customElements.define('pp-announcement', ProjectPetAnnouncement);
